import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import {
  AgentActionDto,
  AgentActionOutcome,
  AgentConversationDto,
  AgentConversationListEntryDto,
  AgentMessageDto,
  AgentUserStatusDto,
  isCreateAssetAction,
  isCreateEventAction,
  isCreateTicketAction,
  isNavigateToAssetAction,
  isNavigateToEventAction,
  isNavigateToTicketAction,
  isOpenDocumentAction,
  isUpdateAssetAction,
  isUpdateEventAction,
  isUpdateTicketAction,
  isUpdateTicketStatusAction
} from '../model';
import { AgentApiService } from './agent-api.service';
import { AgentAssetDraftService } from './agent-asset-draft.service';
import { AgentAssetNavigationService } from './agent-asset-navigation.service';
import { AgentContextService } from './agent-context.service';
import { AgentDocumentService } from './agent-document.service';
import { AgentEventDraftService } from './agent-event-draft.service';
import { AgentEventNavigationService } from './agent-event-navigation.service';
import { AgentTicketDraftService } from './agent-ticket-draft.service';
import { AgentTicketNavigationService } from './agent-ticket-navigation.service';

/**
 * Main entry point of the agent: talks to the API, attaches the grounding context and delegates
 * client-side actions chosen by the user to concrete sub-services.
 *
 * Adding a capability: create a service in `service/`, `inject()` it here, add the type, payload and
 * type guard to `model/agent-action.model.ts` (and `isSupportedAgentAction`) and a branch in `handleAction`.
 */
@Injectable({ providedIn: 'root' })
export class AgentService {
  private readonly _api = inject(AgentApiService);
  private readonly _context = inject(AgentContextService);
  private readonly _assetNavigation = inject(AgentAssetNavigationService);
  private readonly _ticketNavigation = inject(AgentTicketNavigationService);
  private readonly _eventNavigation = inject(AgentEventNavigationService);
  private readonly _documents = inject(AgentDocumentService);
  private readonly _assetDrafts = inject(AgentAssetDraftService);
  private readonly _ticketDrafts = inject(AgentTicketDraftService);
  private readonly _eventDrafts = inject(AgentEventDraftService);

  createConversation(title: string | null = null): Observable<AgentConversationDto> {
    return this._api.createConversation({ title });
  }

  getConversations(): Observable<AgentConversationListEntryDto[]> {
    return this._api.getConversations();
  }

  getConversation(conversationId: string): Observable<AgentConversationDto> {
    return this._api.getConversation(conversationId);
  }

  /** Whether the assistant is enabled for the user and how many tokens are left; uses no tokens. */
  getUserStatus(): Observable<AgentUserStatusDto> {
    return this._api.getUserStatus();
  }

  /**
   * Sends a message with the current context and resolves with the assistant reply once the backend
   * has it (non-streaming). Actions carried by the reply are not executed here; the chat renders them
   * as cards and calls `handleAction` when the user picks one.
   */
  sendMessage(conversationId: string, message: string): Observable<AgentMessageDto> {
    return this._context
      .getContext$()
      .pipe(switchMap((context) => this._api.sendMessage({ conversationId, message, context })));
  }

  /**
   * Performs an action chosen by the user and resolves with its outcome: navigation cards resolve `opened` right
   * away, draft cards once their form is saved (`saved`) or closed without saving (`dismissed`).
   * Rejects when the type is unsupported or the payload is malformed.
   */
  async handleAction(action: AgentActionDto): Promise<AgentActionOutcome> {
    if (isNavigateToAssetAction(action)) return this._opened(this._assetNavigation.navigate(action));
    if (isNavigateToTicketAction(action)) return this._opened(this._ticketNavigation.navigate(action));
    if (isNavigateToEventAction(action)) return this._opened(this._eventNavigation.navigate(action));
    if (isOpenDocumentAction(action)) return this._opened(this._documents.open(action));
    if (isCreateAssetAction(action)) return this._assetDrafts.create(action);
    if (isUpdateAssetAction(action)) return this._assetDrafts.update(action);
    if (isCreateTicketAction(action)) return this._ticketDrafts.create(action);
    if (isUpdateTicketAction(action)) return this._ticketDrafts.update(action);
    if (isUpdateTicketStatusAction(action)) return this._ticketDrafts.updateStatus(action);
    if (isCreateEventAction(action)) return this._eventDrafts.create(action);
    if (isUpdateEventAction(action)) return this._eventDrafts.update(action);
    throw new Error(`[ai-agent] unsupported action "${action.type}"`);
  }

  /** Moves the 3D walk to the target of a `NavigateTo*` action. Rejects when the action carries no position. */
  async goToPosition(action: AgentActionDto): Promise<void> {
    if (isNavigateToAssetAction(action)) return this._assetNavigation.goToPosition(action);
    throw new Error(`[ai-agent] no 3D position for action "${action.type}"`);
  }

  private async _opened(done: Promise<void>): Promise<AgentActionOutcome> {
    await done;
    return 'opened';
  }
}
